import React, { useMemo, useState } from "react";
import ModalDialogFrame from "../../../components/ModalDialogFrame";
import Root from "./screens/Root";
import GroupSelect from "./screens/GroupSelect";
import CoverSelect from "./screens/CoverSelect";
import WaitingSelect from "./screens/WaitingSelect";
import { TransitionPresets } from "@react-navigation/stack";
import { createPlatformStackNavigator } from "../../../navigations/createPlatformStackNavigator";
import { Buildable } from "ts-essentials";
import { Cct } from "../../../__generated__/graphql";
import {
    IJMEditorStackParamList,
    IJMEditorContextType,
    IjmSalaryInfo,
    IjmSelection,
} from "./types";
import { IJMEditorContext, useIJMEditorContext } from "./context";

// Re-export for convenience
export type { IjmSelection, IJMEditorStackParamList, IjmSalaryInfo };
export { useIJMEditorContext };

type IJMEditorNavigatorProps = {
    ijmSelection?: Buildable<IjmSelection>;
    salaryInfo?: IjmSalaryInfo;
    useLse: boolean;
    cct?: Cct;
    onSave?: (selection: IjmSelection | null) => void | Promise<void>;
};

// Web (JS stack) slides up from the bottom to match the native `slide_from_bottom` animation.
const Stack = createPlatformStackNavigator<IJMEditorStackParamList>({
    webTransitionPreset: TransitionPresets.ModalSlideFromBottomIOS,
});

/**
 * IJMEditorNavigator is a stack navigator for the IJM editor it is used through the use-ijm-editor.ts hook
 * You should never call this component navigation directly, it is used in the IJMEditorNavScreen
 * @constructor
 */
const IJMEditorNavigator: React.FC<IJMEditorNavigatorProps> = ({
    ijmSelection,
    salaryInfo,
    useLse,
    cct,
    onSave,
}) => {
    const [selection, setSelection] = useState<Buildable<IjmSelection>>(ijmSelection || {});

    const contextValue = useMemo<IJMEditorContextType>(
        () => ({
            selection,
            setSelection,
            cct,
            useLse,
            salaryInfo,
            onSave,
        }),
        [selection, salaryInfo, onSave, cct, useLse],
    );

    return (
        <IJMEditorContext.Provider value={contextValue}>
            <ModalDialogFrame>
                <Stack.Navigator
                    initialRouteName="IJM"
                    screenOptions={{
                        headerShown: false,
                        gestureEnabled: false,
                    }}
                    // Native drives the slide via `animation`; web slides via the transition preset.
                    nativeScreenOptions={{ animation: "slide_from_bottom" }}
                    webScreenOptions={{
                        detachPreviousScreen: false,
                        cardStyle: { height: "100%" },
                    }}
                >
                    <Stack.Screen name="IJM" component={Root} />
                    <Stack.Screen name="GroupSelect" component={GroupSelect} />
                    <Stack.Screen name="WaitingSelect" component={WaitingSelect} />
                    <Stack.Screen name="CoverSelect" component={CoverSelect} />
                </Stack.Navigator>
            </ModalDialogFrame>
        </IJMEditorContext.Provider>
    );
};

export default IJMEditorNavigator;
